import {
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowUp,
  Sort,
} from "@mui/icons-material";
import {
  Button,
  Checkbox,
  ClickAwayListener,
  Dialog,
  FormControlLabel,
  Radio,
  RadioGroup,
  Snackbar,
  Tooltip,
} from "@mui/material";
import cx from "classnames";
import { difference, groupBy, max, multiply, sortBy, uniqBy } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ReactComponent as CrownIcon } from "assets/crown.svg";
import {
  ReactComponent as Close,
  ReactComponent as XMark,
} from "assets/x-mark.svg";
import ShotContext from "components/Shot/ShotContext";
import ThrottlePopup from "components/ThrottlePopup";
import {
  DEPTH_MAP_EXPORT_MODES,
  EXPORT_MODES,
  PLANS,
  RETIME_EXPORT_MODES,
  SERVICES,
  SHOT_SOURCES,
  SMART_VECTORS_EXPORT_MODES,
  TRACKING_EXPORT_MODES,
} from "constants/constants";
import {
  SLAPSHOT_DOWNLOADS,
  SLAPSHOT_MY_PLAN_UPGRADE_URL,
  SLAPSHOT_SUBSCRIPTION_UPGRADE_URL,
} from "constants/urls";
import { UserContext } from "layouts/SlapshotMainLayout";
import { Price } from "models/Payment";
import { createExport, getBulkExportLayers } from "services/ExportService";
import {
  formatPrice,
  getRelativeDate,
  getSingularOrPlural,
  isShotBillable,
} from "utils/helper";
import { useCountdown } from "utils/hooks";

import styles from "./BulkExport.module.scss";

type BulkExportProps = {
  setShowBulkExportModal: React.Dispatch<React.SetStateAction<boolean>>;
};
type Shot = {
  id: number;
  name: string;
  shot_preview_image_url: string;
  frame_count: number;
  resolution: number;
  source: string;
  is_demo_shot: boolean;
};
type Layer = {
  id: number;
  name: string;
  cover_image_url: string;
  shot_id: number;
  inference_id: number;
  frame_number: number;
  last_export: string | null;
  export_count: number;
  is_autopilot_layer: boolean;
};

enum FilterOptions {
  ALL = "All outputs",
  NEW = "New outputs",
}

type ExportShotProps = {
  shot: Shot;
  layers: Layer[];
  layersToExport: Layer[];
  setLayersToExport: React.Dispatch<React.SetStateAction<Layer[]>>;
};

const ExportShot: React.FC<ExportShotProps> = ({
  shot,
  layers,
  layersToExport,
  setLayersToExport,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { service } = useContext(ShotContext);
  const [trackingLayerGroups, setTrackingLayerGroups] = useState<
    Record<string, Layer[]>
  >({});

  const lastExportDate = useMemo(
    () => max(layers.map((l) => l.last_export)),
    [layers],
  );

  const handleLayerClick = (layer: Layer, isChecked: boolean) => {
    if (isChecked) {
      setLayersToExport((oldLayers) =>
        Array.from(new Set([...oldLayers, layer])),
      );
    } else {
      setLayersToExport((oldLayers) =>
        oldLayers.filter((l) => l.id !== layer.id),
      );
    }
  };

  const handleLayerGroupClick = (layerGroup: Layer[], isChecked: boolean) => {
    if (isChecked) {
      setLayersToExport((oldLayers) =>
        Array.from(new Set([...oldLayers, ...layerGroup])),
      );
    } else {
      setLayersToExport((oldLayers) =>
        oldLayers.filter((l) => !layerGroup.some((layer) => layer.id === l.id)),
      );
    }
  };

  useEffect(() => {
    if (service === SERVICES.TRACKING) {
      const grouped: Record<string, Layer[]> = {};
      layers.forEach((layer, index) => {
        const key =
          layer.inference_id === null
            ? `null-${index}`
            : `inference-${layer.inference_id}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(layer);
      });
      setTrackingLayerGroups(grouped);
    }
  }, [service, layers]);

  return (
    <div className={styles.shotCon}>
      <div
        className={styles.shotHeadCon}
        onClick={() => setExpanded(!expanded)}
      >
        <Checkbox
          sx={{
            color: "#E8EAED",
            "&.Mui-checked": {
              color: "#6565EC",
            },
            "& .MuiSvgIcon-root": { fontSize: 18 },
            width: "18px",
            height: "18px",
          }}
          onClick={(e) => e.stopPropagation()}
          onChange={(e, v) => {
            if (v) {
              setLayersToExport((oldLayers) =>
                Array.from(new Set([...oldLayers, ...layers])),
              );
            } else {
              setLayersToExport((oldLayers) =>
                oldLayers.filter((l) => l.shot_id !== shot.id),
              );
            }
          }}
          checked={difference(layers, layersToExport).length < layers.length}
        />

        <img
          className={styles.shotPreview}
          src={shot.shot_preview_image_url}
          alt={shot.name}
        />

        <div className={styles.shotNameCon}>
          <div className={styles.shotName}>{shot.name}</div>

          <div className={styles.layersDetails}>
            <span>
              {`${layers.length} ${getSingularOrPlural(
                layers.length,
                service === SERVICES.RETIME ? "retimed shot" : "layer",
              )}`}
            </span>

            {lastExportDate && (
              <>
                <span style={{ margin: "0 8px" }}>|</span>

                <span>
                  {getRelativeDate(new Date(lastExportDate), "Last exported")}
                </span>
              </>
            )}
          </div>
        </div>

        <div className={styles.expander}>
          {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </div>
      </div>

      {expanded && (
        <>
          <div className={styles.shotDivider} />
          <div className={styles.layersCon}>
            {service !== SERVICES.TRACKING
              ? layers.map((layer) => {
                  const checked =
                    layersToExport.findIndex((l) => l.id === layer.id) > -1;
                  return (
                    <div
                      key={layer.id}
                      className={styles.layerCon}
                      onClick={() => {
                        handleLayerClick(layer, !checked);
                      }}
                    >
                      <img
                        className={styles.layerPreview}
                        src={layer.cover_image_url}
                        alt={layer.name}
                      />
                      <div className={styles.layerName}>{layer.name}</div>
                      <Checkbox
                        sx={{
                          color: "#E8EAED",
                          "&.Mui-checked": {
                            color: "#6565EC",
                          },
                          "& .MuiSvgIcon-root": { fontSize: 16 },
                          width: "16px",
                          height: "16px",
                        }}
                        onChange={(e, v) => {
                          handleLayerClick(layer, v);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        checked={checked}
                      />
                    </div>
                  );
                })
              : Object.entries(trackingLayerGroups).map(([key, layerGroup]) => {
                  const checked = layerGroup.every((layer) =>
                    layersToExport.some((l) => l.id === layer.id),
                  );
                  const layer = layerGroup[0];
                  return (
                    <div
                      key={key}
                      className={styles.layerCon}
                      onClick={() => {
                        handleLayerGroupClick(layerGroup, !checked);
                      }}
                    >
                      <img
                        className={styles.layerPreview}
                        src={layer.cover_image_url}
                        alt={layer.name}
                      />
                      <div className={styles.layerName}>
                        Keyframe:{" "}
                        <span>
                          {layerGroup
                            .map((l) => l.frame_number)
                            .sort((a, b) => a - b)
                            .join(", ")}
                        </span>
                      </div>
                      <Checkbox
                        sx={{
                          color: "#E8EAED",
                          "&.Mui-checked": {
                            color: "#6565EC",
                          },
                          "& .MuiSvgIcon-root": { fontSize: 16 },
                          width: "16px",
                          height: "16px",
                        }}
                        onChange={(e, v) => {
                          handleLayerGroupClick(layerGroup, !checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        checked={checked}
                      />
                    </div>
                  );
                })}
          </div>
        </>
      )}
    </div>
  );
};

export const ExportMessage: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const navigate = useNavigate();

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      autoHideDuration={6000}
      onClose={(event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
          return;
        }
        onClose();
      }}
    >
      <div className={styles.exportNotification}>
        <div className={styles.notificationLoader}>
          <img
            src="/loading.gif"
            className={styles.notificationLoadingGif}
            alt=""
          />
        </div>
        <div className={styles.exportNotificationDetails}>
          <h3>Generating the package...</h3>
          <p>
            Your package generation is in process.
            <br />
            You can download it from the Exports page.
          </p>
          <Button
            className={styles.downloadsButton}
            onClick={() => {
              navigate(SLAPSHOT_DOWNLOADS);
            }}
          >
            Go to Exports&nbsp;
            <svg
              width="10"
              height="11"
              viewBox="0 0 10 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.9987 0.833496L4.1762 1.656L7.4312 4.91683H0.332031V6.0835H7.4312L4.1762 9.34433L4.9987 10.1668L9.66536 5.50016L4.9987 0.833496Z"
                fill="#6565EC"
              />
            </svg>
          </Button>
        </div>
      </div>
    </Snackbar>
  );
};

export const ExportWarningDialog = ({
  open,
  onClose,
  onContinue,
  requiredFrames,
  requiredAutopilotFrames,
}: {
  open: boolean;
  onClose: () => void;
  onContinue?: () => void;
  requiredFrames: number;
  requiredAutopilotFrames: number;
}) => {
  const user = useContext(UserContext);
  const { project, selectedShot, subscription } = useContext(ShotContext);
  const navigate = useNavigate();
  const additionalCost: Price | null = useMemo(() => {
    if (
      !subscription ||
      subscription.is_free_trial_period ||
      !subscription.plan.price.unit_amount_decimal ||
      subscription.frames_used + requiredFrames <= subscription.frames_limit
    )
      return null;
    const extraFrames =
      subscription.frames_used > subscription.frames_limit
        ? requiredFrames
        : subscription.frames_used + requiredFrames - subscription.frames_limit;
    return {
      unit_amount_decimal:
        extraFrames * subscription.plan.extra_frame_cost.unit_amount_decimal,
      currency: subscription.plan.price.currency,
    };
  }, [subscription, requiredFrames]);

  const additionalAutopilotCost: Price | null = useMemo(() => {
    if (
      !subscription ||
      subscription.is_free_trial_period ||
      !subscription.plan.price.unit_amount_decimal ||
      subscription.autopilot_frames_used + requiredAutopilotFrames <=
        subscription.autopilot_frames_limit
    )
      return null;
    const extraFrames =
      subscription.autopilot_frames_used > subscription.autopilot_frames_limit
        ? requiredAutopilotFrames
        : subscription.autopilot_frames_used +
          requiredAutopilotFrames -
          subscription.autopilot_frames_limit;
    return {
      unit_amount_decimal:
        extraFrames *
        subscription.plan.autopilot_frame_cost.unit_amount_decimal,
      currency: subscription.plan.price.currency,
    };
  }, [subscription, requiredAutopilotFrames]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          className: "dialog",
        },
      }}
    >
      <div className="modalContainer">
        <div className="modalHeader">
          <h3 className="modalTitle">Export - Overage charges</h3>
          <button onClick={onClose} type="button" className="closeButton">
            <XMark />
          </button>
        </div>
        <div className={styles.qmContent}>
          {requiredFrames > 0 && (
            <>
              <strong>Number of frames exported:</strong>
              <p
                className={styles.qmQuota}
              >{`${subscription?.frames_used} of ${subscription?.frames_limit}`}</p>
            </>
          )}

          {requiredFrames > 0 && requiredAutopilotFrames > 0 && <br />}

          {requiredAutopilotFrames > 0 && (
            <>
              <strong>Number of Autopilot frames exported:</strong>
              <p
                className={styles.qmQuota}
              >{`${subscription?.autopilot_frames_used} of ${subscription?.autopilot_frames_limit}`}</p>
            </>
          )}

          <div className={styles.qmDetails}>
            {requiredFrames > 0 &&
              subscription &&
              (subscription?.frames_used <= subscription?.frames_limit ? (
                <p>
                  {`Your current export action will push you over the export quota by `}
                  <strong>{requiredFrames}</strong>
                  {`. The extra frames will be charged at `}
                  <strong>{`${subscription?.plan.extra_frame_cost && formatPrice(subscription.plan.extra_frame_cost)}/frame ${additionalCost ? `(${formatPrice(additionalCost)})` : ""}`}</strong>
                  .
                </p>
              ) : (
                <p>
                  You have already exhausted your export frames quota for the
                  month. All additional exports will be charged at &nbsp;
                  <strong>{`${subscription?.plan.extra_frame_cost && formatPrice(subscription.plan.extra_frame_cost)}/frame ${additionalCost ? `(${formatPrice(additionalCost)})` : ""}`}</strong>
                </p>
              ))}

            {requiredAutopilotFrames > 0 &&
              subscription &&
              (subscription?.autopilot_frames_used <=
              subscription?.autopilot_frames_limit ? (
                <p>
                  {`Your current export action will push you over the Autopilot export quota by `}
                  <strong>{requiredAutopilotFrames}</strong>
                  {`. The extra Autopilot frames will be charged at `}
                  <strong>{`${subscription?.plan.autopilot_frame_cost && formatPrice(subscription.plan.autopilot_frame_cost)}/frame ${additionalAutopilotCost ? `(${formatPrice(additionalAutopilotCost)})` : ""}`}</strong>
                  .
                </p>
              ) : (
                <p>
                  You have already exhausted your Autopilot export frames quota
                  for the month. All additional Autopilot exports will be
                  charged at &nbsp;
                  <strong>{`${subscription?.plan.autopilot_frame_cost && formatPrice(subscription.plan.autopilot_frame_cost)}/frame ${additionalAutopilotCost ? `(${formatPrice(additionalAutopilotCost)})` : ""}`}</strong>
                </p>
              ))}

            <p>
              You can choose to continue <b>OR</b> upgrade to increase your
              quota and better pricing.
            </p>
          </div>
          <div className={styles.qmDontShow}>
            <Checkbox
              className={styles.qmCheck}
              onChange={(e) => {
                user?.setPreferences(
                  "hideFramesQuotaWarning",
                  e.target.checked,
                  false,
                );
              }}
            />
            <span className={styles.qmDontshowtext}>Don't show this again</span>
          </div>
        </div>
        <div className={styles.qmActions}>
          <Button disableRipple className={styles.qmRetry} onClick={onContinue}>
            Continue with Export
          </Button>
          {project.company.is_admin && (
            <Button
              className={styles.qmUpgrade}
              onClick={() => {
                navigate(
                  project.company.is_owner
                    ? SLAPSHOT_MY_PLAN_UPGRADE_URL
                    : `${SLAPSHOT_SUBSCRIPTION_UPGRADE_URL}?workspaceId=${project.company.id}&workspaceName=${project.company.name}`,
                );
              }}
            >
              <CrownIcon className={styles.crownIcon} />
              Upgrade
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

const BulkExport: React.FC<BulkExportProps> = ({ setShowBulkExportModal }) => {
  const { project, service, subscription, throttleUsage, getThrottleUsage } =
    useContext(ShotContext);
  const user = useContext(UserContext);
  const navigate = useNavigate();

  const [shots, setShots] = useState<Shot[]>([]);
  const [layers, setLayers] = useState<Layer[]>([]);
  const [layersToExport, setLayersToExport] = useState<Layer[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMessage, setShowExportMessage] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterBy, setFilterBy] = useState<keyof typeof FilterOptions>("ALL");
  const [showExportQuotaWarning, setShowExportQuotaWarning] = useState(false);
  const [shouldUpgradeForExport, setShouldUpgradeForExport] = useState("");
  const [requiredFrames, setrequiredFrames] = useState(0);
  const [requiredAutopilotFrames, setrequiredAutopilotFrames] = useState(0);
  const [showThrottleDialog, setShowThrottleDialog] = useState(false);

  const throttledTill = useMemo(() => {
    if (
      throttleUsage?.is_export_throttled &&
      (throttleUsage.allowed_exports || 0) < 1 &&
      throttleUsage.next_export_provision_at
    ) {
      return new Date(throttleUsage.next_export_provision_at);
    }
    return null;
  }, [throttleUsage]);
  const countdown = useCountdown(throttledTill, getThrottleUsage);

  const isExportDisabled = useMemo(
    () => isExporting || layersToExport.length === 0 || !!throttledTill,
    [isExporting, layersToExport.length, throttledTill],
  );

  const SERVICE_EXPORT_MODES = (() => {
    switch (service) {
      case SERVICES.DEPTH_MAP:
        return DEPTH_MAP_EXPORT_MODES;
      case SERVICES.SMART_VECTORS:
        return SMART_VECTORS_EXPORT_MODES;
      case SERVICES.RETIME:
        return RETIME_EXPORT_MODES;
      case SERVICES.TRACKING:
        return TRACKING_EXPORT_MODES;
      default:
        return EXPORT_MODES;
    }
  })();

  const DEFAULT_EXPORT_MODE = (() => {
    switch (service) {
      case SERVICES.DEPTH_MAP:
        return DEPTH_MAP_EXPORT_MODES.JPG_MODE;
      case SERVICES.SMART_VECTORS:
        return SMART_VECTORS_EXPORT_MODES.EXR_MODE;
      case SERVICES.RETIME:
        return RETIME_EXPORT_MODES.MOV_MODE;
      case SERVICES.TRACKING:
        return TRACKING_EXPORT_MODES.ALL_MODE;
      default:
        return EXPORT_MODES.JPG_MODE;
    }
  })();

  const exportModes = useMemo(() => {
    const saved =
      user?.preferences?.exportModes && user?.preferences?.exportModes[service];
    return saved?.length ? saved : [DEFAULT_EXPORT_MODE];
  }, [user?.preferences?.exportModes, service, DEFAULT_EXPORT_MODE]);

  const changeExportMode = useCallback(
    (
      newMode:
        | EXPORT_MODES
        | DEPTH_MAP_EXPORT_MODES
        | SMART_VECTORS_EXPORT_MODES
        | RETIME_EXPORT_MODES
        | TRACKING_EXPORT_MODES,
    ) => {
      user?.setPreferences(
        "exportModes",
        {
          ...user.preferences?.exportModes,
          [service]: [newMode],
        },
        false,
      );
    },
    [user, service],
  );

  const onExport = useCallback(
    async (layerToBeExported: Layer[]) => {
      setIsExporting(true);
      const layerIds = layerToBeExported.map((layer) => layer.id);
      const { id } = project;
      createExport(+id, layerIds, exportModes)
        .then(() => {
          setLayers((prev) => {
            return prev.map((layer) => {
              if (layerIds.includes(layer.id)) {
                return { ...layer, export_count: layer.export_count + 1 };
              }
              return layer;
            });
          });
          setShowExportMessage(true);
          getThrottleUsage();
        })
        .catch((err) => {
          if (err.detail) toast.error(err.detail);
          else toast.error("Something went wrong");
        })
        .finally(() => {
          setLayersToExport([]);
          setIsExporting(false);
        });
    },
    [exportModes, getThrottleUsage, project],
  );

  const onBeforeExport = useCallback(
    (layerToBeExported) => {
      if (!subscription) return;
      if (
        requiredFrames > 0 &&
        subscription.frames_used + requiredFrames > subscription.frames_limit &&
        !user?.preferences?.hideFramesQuotaWarning
      ) {
        setShowExportQuotaWarning(true);
      } else {
        onExport(layerToBeExported);
      }
    },
    [
      onExport,
      requiredFrames,
      subscription,
      user?.preferences?.hideFramesQuotaWarning,
    ],
  );

  useEffect(() => {
    if (!subscription || subscription.is_free_trial_period) return;
    let shouldUpgrade = "";

    // resolution check
    shots
      .filter((shot) =>
        layersToExport.some((layer) => layer.shot_id === shot.id),
      )
      .forEach((shot) => {
        if (
          isShotBillable(shot) &&
          shot.resolution >
            subscription.plan.resolution.dimension.reduce(multiply)
        )
          shouldUpgrade = `Max resolution allowed for your plan is ${subscription.plan.resolution.dimension.join("x")}`;
      });

    let framesRequired = 0;
    let autopilotFramesRequired = 0;

    if ([SERVICES.TRACKING].includes(service)) {
      Object.values(groupBy(layersToExport, "shot_id")).forEach(
        (groupedLayers) => {
          const autopilotLayers = groupedLayers.filter(
            (layer) => layer.is_autopilot_layer,
          );
          const nonAutopilotLayers = groupedLayers.filter(
            (layer) => !layer.is_autopilot_layer,
          );

          autopilotLayers.forEach((layer) => {
            const layerShot = shots.find((shot) => shot.id === layer.shot_id);
            if (
              layerShot &&
              isShotBillable(layerShot) &&
              layer.export_count === 0
            )
              autopilotFramesRequired += layerShot.frame_count || 0;
          });

          // For tracking, we don't consider the keyframes towards quota
          if (!nonAutopilotLayers.some((layer) => layer.export_count > 0)) {
            const layerShot = shots.find(
              (shot) => shot.id === nonAutopilotLayers[0].shot_id,
            );
            if (layerShot && isShotBillable(layerShot))
              framesRequired += layerShot?.frame_count || 0;
          }
        },
      );
    } else {
      layersToExport.forEach((layer) => {
        const layerShot = shots.find((shot) => shot.id === layer.shot_id);
        if (
          layerShot &&
          isShotBillable(layerShot) &&
          layer.export_count === 0
        ) {
          if (layer.is_autopilot_layer)
            autopilotFramesRequired += layerShot?.frame_count || 0;
          else framesRequired += layerShot?.frame_count || 0;
        }
      });
    }

    // export frames check for free plan
    if (
      !subscription.plan.extra_frame_cost.unit_amount_decimal &&
      subscription.frames_used + framesRequired > subscription.frames_limit
    ) {
      shouldUpgrade = `Max frames allowed for your plan is ${subscription.frames_limit}`;
    }

    // autopilot export frames check for free plan
    if (
      !subscription.plan.autopilot_frame_cost.unit_amount_decimal &&
      subscription.autopilot_frames_used + autopilotFramesRequired >
        subscription.autopilot_frames_limit
    ) {
      shouldUpgrade = `Max Autopilot frames allowed for your plan is ${subscription.autopilot_frames_limit}`;
    }

    setrequiredFrames(framesRequired);
    setrequiredAutopilotFrames(autopilotFramesRequired);
    setShouldUpgradeForExport(shouldUpgrade);
  }, [layers, layersToExport, service, shots, subscription]);

  useEffect(() => {
    getBulkExportLayers(project.id, service, filterBy).then((data) => {
      setLayersToExport([]);
      setShots(data.shots);
      setLayers(
        data.layers.filter(
          (l) => !l.is_autopilot_layer || l.export_count === 0,
        ),
      );
    });
  }, [filterBy, project, service]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.modalCon}>
          <div
            className={styles.back}
            onClick={() => {
              setShowBulkExportModal(false);
            }}
          >
            <KeyboardArrowLeft />
            Back
          </div>

          <div className={styles.heading}>
            Select shots that you want to export:
          </div>
          <div className={styles.subHeading}>
            You can select multiple shots and also unselect layers within those
            shots in the next step.
          </div>

          <div className={styles.allShotsCon}>
            <div>
              {!!shots.length && (
                <label className={styles.allShotsSelector}>
                  <Checkbox
                    sx={{
                      color: "#E8EAED",
                      "&.Mui-checked": {
                        color: "#6565EC",
                      },
                      "& .MuiSvgIcon-root": { fontSize: 18 },
                      width: "18px",
                      height: "18px",
                    }}
                    onChange={(e, v) => {
                      if (v) {
                        setLayersToExport(layers);
                      } else {
                        setLayersToExport([]);
                      }
                    }}
                    checked={layersToExport.length === layers.length}
                  />
                  Selected shots (
                  {new Set(layersToExport.map((l) => l.shot_id)).size})
                </label>
              )}
            </div>

            <div className={styles.optionsContainer}>
              {!!shots.length &&
                service !== SERVICES.RETIME &&
                service !== SERVICES.TRACKING &&
                service !== SERVICES.SMART_VECTORS && (
                  <div className={styles.exportModesCon}>
                    <div>Export as:</div>
                    <div className={styles.viewTabs}>
                      {Object.entries(SERVICE_EXPORT_MODES).map(
                        ([key, value]) => (
                          <Button
                            key={key}
                            onClick={() => changeExportMode(value)}
                            size="small"
                            className={cx(
                              styles.viewTab,
                              exportModes.includes(value) && styles.tabActive,
                            )}
                          >
                            {value}
                          </Button>
                        ),
                      )}
                    </div>
                  </div>
                )}

              <div>
                <ClickAwayListener
                  onClickAway={() => setShowFilterModal(false)}
                >
                  <Tooltip
                    open={showFilterModal}
                    arrow
                    placement="bottom"
                    slotProps={{
                      tooltip: {
                        sx: {
                          padding: "0 !important",
                          bgcolor: "#22242A",
                          borderRadius: "8px",
                          "& .MuiTooltip-arrow": {
                            color: "#22242A",
                          },
                          border: "1px solid #CCCCDC1F",
                          boxShadow: "0px 8px 24px 0px #010409",
                        },
                      },
                    }}
                    title={
                      <div
                        className={styles.filterTooltip}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className={styles.filterTooltipTitle}>
                          Service:
                        </div>

                        <RadioGroup
                          aria-label="service"
                          name="service"
                          value={filterBy}
                          onChange={(e) => {
                            setFilterBy(
                              e.target.value as keyof typeof FilterOptions,
                            );
                            setTimeout(() => {
                              setShowFilterModal(false);
                            }, 300);
                          }}
                        >
                          {Object.entries(FilterOptions).map(([key, value]) => (
                            <FormControlLabel
                              key={key}
                              value={key}
                              control={
                                <Radio
                                  sx={{
                                    color: "#CCCCDCA6",
                                    "&.Mui-checked": {
                                      color: "#6565EC",
                                    },
                                    "& .MuiSvgIcon-root": {
                                      fontSize: "14px",
                                    },
                                  }}
                                />
                              }
                              label={value}
                              sx={{
                                "&.MuiFormControlLabel-root": {
                                  margin: "0px !important",
                                  marginLeft: "-10px !important",
                                  width: "fit-content !important",
                                },
                                "& .MuiFormControlLabel-label": {
                                  color: "#CCCCDC",
                                  fontSize: "14px",
                                  fontFamily: "Inter",
                                  marginLeft: "6px",
                                },
                                "& .Mui-checked + .MuiFormControlLabel-label": {
                                  color: "#FFFFFFE5",
                                  fontWeight: 500,
                                },
                              }}
                            />
                          ))}
                        </RadioGroup>
                      </div>
                    }
                  >
                    <div
                      className={styles.filterTrigger}
                      onClick={() => setShowFilterModal(!showFilterModal)}
                    >
                      <span>Filter:</span>
                      <div>{FilterOptions[filterBy]}</div>
                      <KeyboardArrowDown />
                    </div>
                  </Tooltip>
                </ClickAwayListener>
              </div>
            </div>
          </div>

          <div className={styles.shotsCon}>
            {shots.length ? (
              shots.map((shot) => {
                const layersForShot = layers.filter(
                  (l) => l.shot_id === shot.id,
                );
                if (layersForShot.length === 0) return null;
                return (
                  <ExportShot
                    key={shot.id}
                    shot={shot}
                    layers={layersForShot}
                    layersToExport={layersToExport}
                    setLayersToExport={setLayersToExport}
                  />
                );
              })
            ) : (
              <div className={styles.noShots}>
                {filterBy === "NEW"
                  ? "No new outputs!"
                  : "No outputs to export!"}
              </div>
            )}
          </div>

          <div className={styles.actions}>
            <div
              className={styles.cancelBtn}
              onClick={() => {
                setShowBulkExportModal(false);
              }}
            >
              Cancel
            </div>

            {subscription &&
              (shouldUpgradeForExport ? (
                <div className={styles.upgradeBtncon}>
                  <Tooltip
                    title={shouldUpgradeForExport}
                    placement="top"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "#2b2b2b",
                          maxWidth: "400px",
                          borderRadius: "8px",
                          "& .MuiTooltip-arrow": {
                            color: "#2b2b2b",
                          },
                          padding: "8px",
                        },
                      },
                    }}
                  >
                    <Button
                      disabled={!project.company.is_admin}
                      className={styles.upgrade}
                      onClick={() =>
                        navigate(
                          project.company.is_owner
                            ? SLAPSHOT_MY_PLAN_UPGRADE_URL
                            : `${SLAPSHOT_SUBSCRIPTION_UPGRADE_URL}?workspaceId=${project.company.id}&workspaceName=${project.company.name}`,
                        )
                      }
                    >
                      <CrownIcon className={styles.crownIcon} />
                      Upgrade to export
                    </Button>
                  </Tooltip>
                </div>
              ) : (
                <Button
                  disabled={isExportDisabled}
                  onClick={() => {
                    if (
                      throttleUsage?.is_export_throttled &&
                      (throttleUsage?.allowed_exports || 0) <
                        layersToExport.length
                    ) {
                      setShowThrottleDialog(true);
                    } else {
                      onBeforeExport(layersToExport);
                    }
                  }}
                  className={cx(styles.slapShotButton, styles.exportBtn)}
                >
                  {countdown ? `Export in ${countdown}` : "Export"}
                </Button>
              ))}
          </div>
        </div>
      </div>

      {showThrottleDialog && (
        <ThrottlePopup
          layers={
            [SERVICES.TRACKING].includes(service)
              ? uniqBy(layersToExport, "shot_id")
              : layersToExport
          }
          context="Export"
          onClose={() => setShowThrottleDialog(false)}
          onSubmit={(layerIds) => {
            setShowThrottleDialog(false);
            let layersToBeExported: Layer[] = [];
            if ([SERVICES.TRACKING].includes(service)) {
              const selectedShots = shots
                .filter((shot) => layerIds.includes(shot.id))
                .map((shot) => shot.id);
              layersToBeExported = layers.filter((layer) =>
                selectedShots.includes(layer.shot_id),
              );
            } else {
              layersToBeExported = layersToExport.filter((layer) =>
                layerIds.includes(layer.id),
              );
            }
            setLayersToExport(layersToBeExported);
            onBeforeExport(layersToBeExported);
          }}
        />
      )}

      {showExportMessage && (
        <ExportMessage
          onClose={() => {
            setShowExportMessage(false);
            setShowBulkExportModal(false);
          }}
        />
      )}

      {showExportQuotaWarning && (
        <ExportWarningDialog
          open={showExportQuotaWarning}
          onClose={() => setShowExportQuotaWarning(false)}
          onContinue={() => {
            setShowExportQuotaWarning(false);
            onExport(layersToExport);
          }}
          requiredFrames={requiredFrames}
          requiredAutopilotFrames={requiredAutopilotFrames}
        />
      )}
    </>
  );
};

export default BulkExport;
